import Phaser from 'phaser';
import { COLORS, VIEW_HEIGHT, VIEW_WIDTH } from './constants';
import {
  loadLeaderboard,
  loadNickname,
  saveNickname,
  type LeaderboardEntry,
} from './playerProfile';

export class MenuScene extends Phaser.Scene {
  private starting = false;
  private leaderboardOverlay?: Phaser.GameObjects.Container;

  constructor() {
    super('menu');
  }

  create(): void {
    this.starting = false;
    this.leaderboardOverlay = undefined;
    this.cameras.main.setBackgroundColor(COLORS.void);
    const nickname = loadNickname(window.localStorage);

    const backdrop = this.add.graphics();
    backdrop.fillGradientStyle(0x17110f, 0x17110f, 0x070a09, 0x070a09, 1);
    backdrop.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
    backdrop.fillStyle(0x4f2528, 0.14).fillCircle(VIEW_WIDTH / 2, 205, 170);
    backdrop.lineStyle(1, 0x8c7957, 0.1);
    for (let x = -VIEW_HEIGHT; x < VIEW_WIDTH; x += 46) {
      backdrop.lineBetween(x, 0, x + VIEW_HEIGHT, VIEW_HEIGHT);
    }

    this.drawOrderSigil(VIEW_WIDTH / 2, 218);

    this.add
      .text(VIEW_WIDTH / 2, 54, 'KUOGAM', {
        fontFamily: 'Georgia, serif',
        fontSize: '46px',
        fontStyle: 'bold',
        color: '#e3d2a5',
        stroke: '#21120f',
        strokeThickness: 8,
        shadow: { color: '#9a3e32', blur: 16, fill: true },
        letterSpacing: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(VIEW_WIDTH / 2, 100, 'ПРОКЛЯТАЯ ЗЕМЛЯ', {
        fontFamily: 'Georgia, serif',
        fontSize: '13px',
        color: '#988765',
        letterSpacing: 6,
      })
      .setOrigin(0.5);

    this.add
      .text(VIEW_WIDTH / 2, 359, 'ИЗГНАННИК', {
        fontFamily: 'Georgia, serif',
        fontSize: '16px',
        color: '#b6aa8d',
        letterSpacing: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(VIEW_WIDTH / 2, 386, 'Выживи среди мёртвых. Закаляй оружие и навыки.', {
        fontFamily: 'Georgia, serif',
        fontSize: '13px',
        color: '#d0c2a0',
        align: 'center',
        wordWrap: { width: 360 },
      })
      .setOrigin(0.5);

    const profileBox = this.add
      .rectangle(VIEW_WIDTH / 2, 432, 326, 50, nickname ? 0x171b18 : 0x2c211c, 1)
      .setStrokeStyle(2, nickname ? 0x706750 : 0xa26948, 0.84)
      .setInteractive({ useHandCursor: true });
    const profileTitle = this.add
      .text(78, 422, nickname ? 'ИГРОК' : 'ВХОД В ИГРУ', {
        fontSize: '9px',
        fontStyle: 'bold',
        color: '#777162',
        letterSpacing: 2,
      })
      .setOrigin(0, 0.5);
    const profileName = this.add
      .text(78, 441, nickname ?? 'ВВЕДИ НИК', {
        fontFamily: 'Georgia, serif',
        fontSize: '15px',
        fontStyle: 'bold',
        color: nickname ? '#e2d3ad' : '#d49a68',
      })
      .setOrigin(0, 0.5);
    const profileAction = this.add
      .text(372, 432, nickname ? 'СМЕНИТЬ ›' : 'ВОЙТИ ›', {
        fontSize: '10px',
        fontStyle: 'bold',
        color: nickname ? '#9e9279' : '#e0b17b',
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });

    let nicknamePromptOpen = false;
    let nicknamePromptAttempted = false;
    const requestNickname = (): string | null => {
      if (nicknamePromptOpen) return null;
      nicknamePromptAttempted = true;
      nicknamePromptOpen = true;
      const requested = window.prompt(
        'Введите ник: 2–16 символов, кириллица или латиница.',
        nickname ?? '',
      );
      nicknamePromptOpen = false;
      if (requested === null) return null;
      const saved = saveNickname(window.localStorage, requested);
      if (!saved || saved.length < 2) {
        profileBox.setStrokeStyle(3, 0xa94d43, 1);
        profileName.setText('НИК СЛИШКОМ КОРОТКИЙ').setColor('#df7770');
        return null;
      }
      this.scene.restart();
      return saved;
    };
    const profileHover = () => profileBox.setFillStyle(0x292b24, 1);
    const profileOut = () => profileBox.setFillStyle(nickname ? 0x171b18 : 0x2c211c, 1);
    profileBox.on('pointerover', profileHover).on('pointerout', profileOut).on('pointerdown', requestNickname);
    profileAction.on('pointerover', profileHover).on('pointerout', profileOut).on('pointerdown', requestNickname);
    if (!nickname) {
      this.time.delayedCall(320, () => {
        if (!nicknamePromptAttempted) requestNickname();
      });
    }

    const startButton = this.add
      .rectangle(VIEW_WIDTH / 2, 508, 326, 62, 0x52282d, 1)
      .setStrokeStyle(2, COLORS.parchmentDark, 0.95)
      .setInteractive({ useHandCursor: true });
    const startLabel = this.add
      .text(VIEW_WIDTH / 2, 508, nickname ? 'НАЧАТЬ ЗАБЕГ' : 'СНАЧАЛА ВВЕДИ НИК', {
        fontFamily: 'Georgia, serif',
        fontSize: '21px',
        fontStyle: 'bold',
        color: '#f0dfb4',
        letterSpacing: 2,
      })
      .setOrigin(0.5);

    const startRun = () => {
      if (this.starting) return;
      if (!nickname) {
        requestNickname();
        return;
      }
      this.starting = true;
      startButton.disableInteractive().setFillStyle(0x352023, 1);
      startLabel.setText('ВОЙТИ ВО ТЬМУ');
      this.cameras.main.fadeOut(240, 6, 8, 7);
      this.time.delayedCall(250, () => this.scene.start('character-select'));
    };

    startButton.on('pointerover', () => startButton.setFillStyle(0x6a3238, 1));
    startButton.on('pointerout', () => startButton.setFillStyle(0x52282d, 1));
    startButton.on('pointerdown', startRun);
    this.input.keyboard?.once('keydown-ENTER', startRun);
    this.input.keyboard?.once('keydown-SPACE', startRun);

    const leaderboardButton = this.add
      .rectangle(VIEW_WIDTH / 2, 581, 326, 50, 0x171b18, 1)
      .setStrokeStyle(2, 0x665f50, 0.72)
      .setInteractive({ useHandCursor: true });
    const leaderboardLabel = this.add
      .text(VIEW_WIDTH / 2, 581, 'ТАБЛИЦА ЛИДЕРОВ', {
        fontFamily: 'Georgia, serif',
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#cfc1a0',
        letterSpacing: 1,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    const showLeaderboard = () => this.showLeaderboard(loadLeaderboard(window.localStorage));
    leaderboardButton.on('pointerover', () => leaderboardButton.setFillStyle(0x292f29, 1));
    leaderboardButton.on('pointerout', () => leaderboardButton.setFillStyle(0x171b18, 1));
    leaderboardButton.on('pointerdown', showLeaderboard);
    leaderboardLabel.on('pointerdown', showLeaderboard);

    this.add
      .text(VIEW_WIDTH / 2, 627, 'WASD / СТРЕЛКИ — ДВИЖЕНИЕ\nАТАКА — АВТОМАТИЧЕСКИ', {
        fontSize: '11px',
        color: '#716b5d',
        align: 'center',
        lineSpacing: 6,
      })
      .setOrigin(0.5, 0);

    this.add
      .text(VIEW_WIDTH / 2, VIEW_HEIGHT - 33, 'VERTICAL SLICE  •  3 МИНУТЫ', {
        fontSize: '10px',
        color: '#514c43',
        letterSpacing: 2,
      })
      .setOrigin(0.5);

    this.cameras.main.fadeIn(220, 6, 8, 7);
  }

  private showLeaderboard(entries: LeaderboardEntry[]): void {
    this.leaderboardOverlay?.destroy(true);
    const overlay = this.add.container(0, 0).setDepth(5000);
    const veil = this.add
      .rectangle(0, 0, VIEW_WIDTH, VIEW_HEIGHT, 0x030504, 0.93)
      .setOrigin(0)
      .setInteractive();
    const panel = this.add
      .rectangle(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, VIEW_WIDTH - 38, VIEW_HEIGHT - 74, 0x111410, 1)
      .setStrokeStyle(3, COLORS.parchmentDark, 0.82)
      .setInteractive();
    const inner = this.add
      .rectangle(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, VIEW_WIDTH - 52, VIEW_HEIGHT - 88, 0x000000, 0)
      .setStrokeStyle(1, 0x4f4b40, 0.62);
    const title = this.add
      .text(VIEW_WIDTH / 2, 78, 'ТАБЛИЦА ЛИДЕРОВ', {
        fontFamily: 'Georgia, serif',
        fontSize: '27px',
        fontStyle: 'bold',
        color: '#eadbb3',
        stroke: '#17100d',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    const subtitle = this.add
      .text(VIEW_WIDTH / 2, 115, 'ЛОКАЛЬНЫЕ РЕКОРДЫ · ЭТОТ БРАУЗЕР', {
        fontSize: '9px',
        fontStyle: 'bold',
        color: '#777162',
        letterSpacing: 1,
      })
      .setOrigin(0.5);
    const header = this.add
      .text(51, 160, '№   ИГРОК', {
        fontSize: '10px',
        fontStyle: 'bold',
        color: '#8e846f',
      });
    const scoreHeader = this.add
      .text(397, 160, 'ОЧКИ', {
        fontSize: '10px',
        fontStyle: 'bold',
        color: '#8e846f',
      })
      .setOrigin(1, 0);
    const rowObjects: Phaser.GameObjects.GameObject[] = [];

    if (entries.length === 0) {
      rowObjects.push(
        this.add
          .text(VIEW_WIDTH / 2, 342, 'ПОКА НЕТ ЗАВЕРШЁННЫХ ЗАБЕГОВ\nТВОЙ ПЕРВЫЙ РЕЗУЛЬТАТ ПОЯВИТСЯ ЗДЕСЬ', {
            fontFamily: 'Georgia, serif',
            fontSize: '14px',
            color: '#746f60',
            align: 'center',
            lineSpacing: 9,
          })
          .setOrigin(0.5),
      );
    } else {
      entries.slice(0, 8).forEach((entry, index) => {
        const y = 196 + index * 58;
        const rankColor = index === 0 ? '#e0c47c' : index < 3 ? '#b9ae91' : '#777b73';
        rowObjects.push(
          this.add
            .rectangle(VIEW_WIDTH / 2, y + 17, 350, 48, index === 0 ? 0x35291d : 0x171a17, 0.78)
            .setStrokeStyle(1, index === 0 ? 0x9a7a48 : 0x464a43, 0.6),
          this.add
            .text(52, y, `${index + 1}`, {
              fontFamily: 'Georgia, serif',
              fontSize: '17px',
              fontStyle: 'bold',
              color: rankColor,
            }),
          this.add
            .text(83, y, entry.nickname, {
              fontSize: '13px',
              fontStyle: 'bold',
              color: '#d8caa7',
            }),
          this.add
            .text(397, y, `${entry.score}`, {
              fontSize: '15px',
              fontStyle: 'bold',
              color: rankColor,
            })
            .setOrigin(1, 0),
          this.add
            .text(83, y + 22, `☠ ${entry.kills}  •  УР. ${entry.level}  •  ${this.formatRunTime(entry.survivedSeconds)}`, {
              fontSize: '9px',
              color: '#747568',
            }),
        );
      });
    }

    const closeButton = this.add
      .rectangle(VIEW_WIDTH / 2, 728, 260, 48, 0x4e292c, 1)
      .setStrokeStyle(2, COLORS.parchmentDark, 0.8)
      .setInteractive({ useHandCursor: true });
    const closeText = this.add
      .text(VIEW_WIDTH / 2, 728, 'ЗАКРЫТЬ', {
        fontFamily: 'Georgia, serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#eadbb3',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    const close = () => {
      overlay.destroy(true);
      if (this.leaderboardOverlay === overlay) this.leaderboardOverlay = undefined;
    };
    closeButton.on('pointerdown', close);
    closeText.on('pointerdown', close);
    veil.on('pointerdown', close);
    overlay.add([veil, panel, inner, title, subtitle, header, scoreHeader, ...rowObjects, closeButton, closeText]);
    this.leaderboardOverlay = overlay;
  }

  private formatRunTime(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  }

  private drawOrderSigil(x: number, y: number): void {
    const sigil = this.add.graphics({ x, y });
    sigil.fillStyle(0x090b0a, 0.82).fillCircle(0, 0, 120);
    sigil.lineStyle(2, 0x8c7957, 0.38).strokeCircle(0, 0, 118);
    sigil.lineStyle(1, 0xd4c49c, 0.18).strokeCircle(0, 0, 96);

    for (let angle = 0; angle < 360; angle += 15) {
      const radians = Phaser.Math.DegToRad(angle);
      const inner = 102;
      const outer = angle % 45 === 0 ? 132 : 122;
      sigil.lineBetween(
        Math.cos(radians) * inner,
        Math.sin(radians) * inner,
        Math.cos(radians) * outer,
        Math.sin(radians) * outer,
      );
    }

    sigil.lineStyle(8, 0x686c66, 1).lineBetween(0, -78, 0, 67);
    sigil.lineStyle(3, 0xb4ab91, 0.85).lineBetween(-3, -76, -3, 66);
    sigil.fillStyle(0x90938b, 1).fillTriangle(0, -104, -12, -74, 12, -74);
    sigil.lineStyle(7, 0x4a342c, 1).lineBetween(-32, 44, 32, 44);
    sigil.fillStyle(0x4f2528, 1).fillCircle(0, 73, 14);
    sigil.lineStyle(3, 0x9d7c55, 0.9).strokeCircle(0, 73, 14);
  }
}
